import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  getDoc,
  setDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';

const COLLECTION_NAME = 'marketingReports';

// Data type constants
const DATA_TYPES = {
  WEBSITE: 'website',
  SOCIAL: 'social',
  EMAIL: 'email',
  CONTENT: 'content',
  PODCAST: 'podcast',
  ADVERTISING: 'advertising',
  LEAD_SOURCE: 'leadSource'
};

/**
 * Get all marketing reports data for the current user
 * @returns {Promise<Object>} Object containing all marketing data by type
 */
export const getMarketingReportsData = async () => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to access marketing reports data');
    }

    const userId = auth.currentUser.uid;
    const reportsRef = collection(db, COLLECTION_NAME);
    
    // For website and advertising entries, also get global entries (where userId is null)
    // For other types, only get user-specific entries
    const [userQuerySnapshot, websiteGlobalQuerySnapshot, advertisingGlobalQuerySnapshot] = await Promise.all([
      getDocs(query(reportsRef, where('userId', '==', userId))),
      getDocs(query(reportsRef, where('userId', '==', null), where('dataType', '==', DATA_TYPES.WEBSITE))),
      getDocs(query(reportsRef, where('userId', '==', null), where('dataType', '==', DATA_TYPES.ADVERTISING)))
    ]);
    
    // Initialize data structure
    const data = {
      websiteEntries: [],
      socialEntries: [],
      emailEntries: [],
      contentEntries: [],
      podcastEntries: [],
      advertisingEntries: [],
      leadSourceEntries: []
    };

    // Process user-specific documents
    userQuerySnapshot.docs.forEach(docSnapshot => {
      const docData = docSnapshot.data();
      const entry = {
        id: docSnapshot.id,
        ...docData,
        createdAt: docData.createdAt?.toDate() || new Date(),
        updatedAt: docData.updatedAt?.toDate() || new Date()
      };

      // Add to appropriate array based on data type
      switch (docData.dataType) {
        case DATA_TYPES.WEBSITE:
          data.websiteEntries.push(entry);
          break;
        case DATA_TYPES.SOCIAL:
          data.socialEntries.push(entry);
          break;
        case DATA_TYPES.EMAIL:
          data.emailEntries.push(entry);
          break;
        case DATA_TYPES.CONTENT:
          data.contentEntries.push(entry);
          break;
        case DATA_TYPES.PODCAST:
          data.podcastEntries.push(entry);
          break;
        case DATA_TYPES.ADVERTISING:
          data.advertisingEntries.push(entry);
          break;
        case DATA_TYPES.LEAD_SOURCE:
          data.leadSourceEntries.push(entry);
          break;
      }
    });

    // Process global website entries (userId is null)
    websiteGlobalQuerySnapshot.docs.forEach(docSnapshot => {
      const docData = docSnapshot.data();
      const entry = {
        id: docSnapshot.id,
        ...docData,
        createdAt: docData.createdAt?.toDate() || new Date(),
        updatedAt: docData.updatedAt?.toDate() || new Date()
      };
      data.websiteEntries.push(entry);
    });

    // Process global advertising entries (userId is null)
    advertisingGlobalQuerySnapshot.docs.forEach(docSnapshot => {
      const docData = docSnapshot.data();
      const entry = {
        id: docSnapshot.id,
        ...docData,
        createdAt: docData.createdAt?.toDate() || new Date(),
        updatedAt: docData.updatedAt?.toDate() || new Date()
      };
      data.advertisingEntries.push(entry);
    });

    // Sort entries by creation date (newest first)
    Object.keys(data).forEach(key => {
      data[key].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    });

    return data;
  } catch (error) {
    console.error('Error getting marketing reports data:', error);
    throw error;
  }
};

/**
 * Add a new marketing report entry
 * @param {string} dataType - The type of data (website, social, email, content, podcast, advertising)
 * @param {Object} entryData - The data for the entry
 * @returns {Promise<Object>} The added entry with ID
 */
export const addMarketingReportEntry = async (dataType, entryData) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to add marketing report entries');
    }

    const userId = auth.currentUser.uid;
    const reportsRef = collection(db, COLLECTION_NAME);
    
    const docData = {
      userId,
      dataType,
      ...entryData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(reportsRef, docData);
    
    return {
      id: docRef.id,
      ...docData,
      createdAt: new Date(),
      updatedAt: new Date()
    };
  } catch (error) {
    console.error('Error adding marketing report entry:', error);
    throw error;
  }
};

/**
 * Update an existing marketing report entry
 * @param {string} entryId - The ID of the entry to update
 * @param {Object} updateData - The data to update
 * @returns {Promise<Object>} The updated entry
 */
export const updateMarketingReportEntry = async (entryId, updateData) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to update marketing report entries');
    }

    const entryRef = doc(db, COLLECTION_NAME, entryId);
    
    // Verify the entry belongs to the current user
    const entryDoc = await getDoc(entryRef);
    if (!entryDoc.exists()) {
      throw new Error('Entry not found');
    }

    const entryData = entryDoc.data();
    if (entryData.userId !== auth.currentUser.uid) {
      throw new Error('Unauthorized: Entry does not belong to current user');
    }

    const updatedData = {
      ...updateData,
      updatedAt: serverTimestamp()
    };

    await updateDoc(entryRef, updatedData);
    
    return {
      id: entryId,
      ...entryData,
      ...updatedData,
      updatedAt: new Date()
    };
  } catch (error) {
    console.error('Error updating marketing report entry:', error);
    throw error;
  }
};

/**
 * Delete a marketing report entry
 * @param {string} entryId - The ID of the entry to delete
 * @returns {Promise<void>}
 */
export const deleteMarketingReportEntry = async (entryId) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to delete marketing report entries');
    }

    const entryRef = doc(db, COLLECTION_NAME, entryId);
    
    // Verify the entry belongs to the current user
    const entryDoc = await getDoc(entryRef);
    if (!entryDoc.exists()) {
      throw new Error('Entry not found');
    }

    const entryData = entryDoc.data();
    if (entryData.userId !== auth.currentUser.uid) {
      throw new Error('Unauthorized: Entry does not belong to current user');
    }

    await deleteDoc(entryRef);
  } catch (error) {
    console.error('Error deleting marketing report entry:', error);
    throw error;
  }
};

/**
 * Get marketing report entries by data type
 * @param {string} dataType - The type of data to retrieve
 * @returns {Promise<Array>} Array of entries for the specified type
 */
export const getMarketingReportEntriesByType = async (dataType) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to access marketing report entries');
    }

    const userId = auth.currentUser.uid;
    const reportsRef = collection(db, COLLECTION_NAME);
    const q = query(
      reportsRef,
      where('userId', '==', userId),
      where('dataType', '==', dataType),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(docSnapshot => ({
      id: docSnapshot.id,
      ...docSnapshot.data(),
      createdAt: docSnapshot.data().createdAt?.toDate() || new Date(),
      updatedAt: docSnapshot.data().updatedAt?.toDate() || new Date()
    }));
  } catch (error) {
    console.error('Error getting marketing report entries by type:', error);
    throw error;
  }
};

/**
 * Get marketing report entries by date range
 * @param {string} startDate - Start date (YYYY-MM-DD format)
 * @param {string} endDate - End date (YYYY-MM-DD format)
 * @param {string} dataType - Optional data type filter
 * @returns {Promise<Array>} Array of entries within the date range
 */
export const getMarketingReportEntriesByDateRange = async (startDate, endDate, dataType = null) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to access marketing report entries');
    }

    const userId = auth.currentUser.uid;
    const reportsRef = collection(db, COLLECTION_NAME);
    
    let q = query(
      reportsRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );

    if (dataType) {
      q = query(
        reportsRef,
        where('userId', '==', userId),
        where('dataType', '==', dataType),
        orderBy('createdAt', 'desc')
      );
    }
    
    const querySnapshot = await getDocs(q);
    
    const startDateObj = new Date(startDate);
    const endDateObj = new Date(endDate);
    endDateObj.setHours(23, 59, 59, 999); // Include the entire end date
    
    return querySnapshot.docs
      .map(docSnapshot => ({
        id: docSnapshot.id,
        ...docSnapshot.data(),
        createdAt: docSnapshot.data().createdAt?.toDate() || new Date(),
        updatedAt: docSnapshot.data().updatedAt?.toDate() || new Date()
      }))
      .filter(entry => {
        const entryDate = new Date(entry.createdAt);
        return entryDate >= startDateObj && entryDate <= endDateObj;
      });
  } catch (error) {
    console.error('Error getting marketing report entries by date range:', error);
    throw error;
  }
};

/**
 * Get aggregated marketing metrics
 * @returns {Promise<Object>} Object containing aggregated metrics
 */
export const getMarketingMetrics = async () => {
  try {
    const data = await getMarketingReportsData();
    
    // Calculate website metrics
    const totalVisitors = data.websiteEntries.reduce((sum, entry) => sum + (parseInt(entry.visitors) || 0), 0);
    const avgSessionDuration = data.websiteEntries.length > 0 
      ? data.websiteEntries.reduce((sum, entry) => sum + (parseFloat(entry.sessionDuration) || 0), 0) / data.websiteEntries.length
      : 0;
    const avgBounceRate = data.websiteEntries.length > 0 
      ? data.websiteEntries.reduce((sum, entry) => sum + (parseFloat(entry.bounceRate) || 0), 0) / data.websiteEntries.length
      : 0;

    // Calculate social media metrics
    const socialMetrics = data.socialEntries.reduce((acc, entry) => {
      if (!acc[entry.platform]) {
        acc[entry.platform] = 0;
      }
      acc[entry.platform] += parseInt(entry.followers) || 0;
      return acc;
    }, {});

    // Calculate email metrics
    const totalSent = data.emailEntries.reduce((sum, entry) => sum + (parseInt(entry.sent) || 0), 0);
    const totalOpened = data.emailEntries.reduce((sum, entry) => sum + (parseInt(entry.opened) || 0), 0);
    const totalClicked = data.emailEntries.reduce((sum, entry) => sum + (parseInt(entry.clicked) || 0), 0);
    const avgOpenRate = totalSent > 0 ? (totalOpened / totalSent) * 100 : 0;
    const avgClickRate = totalOpened > 0 ? (totalClicked / totalOpened) * 100 : 0;

    // Calculate content metrics
    const totalViews = data.contentEntries.reduce((sum, entry) => sum + (parseInt(entry.views) || 0), 0);
    const avgTimeOnPage = data.contentEntries.length > 0 
      ? data.contentEntries.reduce((sum, entry) => sum + (parseFloat(entry.timeOnPage) || 0), 0) / data.contentEntries.length
      : 0;

    // Calculate podcast metrics
    const totalDownloads = data.podcastEntries.reduce((sum, entry) => sum + (parseInt(entry.downloads) || 0), 0);
    const totalSubscribers = data.podcastEntries.reduce((sum, entry) => sum + (parseInt(entry.subscribers) || 0), 0);
    const totalEpisodes = data.podcastEntries.reduce((sum, entry) => sum + (parseInt(entry.episodes) || 0), 0);
    const avgListenDuration = data.podcastEntries.length > 0 
      ? data.podcastEntries.reduce((sum, entry) => sum + (parseFloat(entry.listenDuration) || 0), 0) / data.podcastEntries.length
      : 0;

    // Calculate advertising metrics
    const totalAdSpend = data.advertisingEntries.reduce((sum, entry) => sum + (parseFloat(entry.adSpend) || 0), 0);

    return {
      website: {
        totalVisitors,
        avgSessionDuration: parseFloat(avgSessionDuration.toFixed(1)),
        avgBounceRate: parseFloat(avgBounceRate.toFixed(1))
      },
      social: socialMetrics,
      email: {
        totalSent,
        totalOpened,
        totalClicked,
        avgOpenRate: parseFloat(avgOpenRate.toFixed(1)),
        avgClickRate: parseFloat(avgClickRate.toFixed(1))
      },
      content: {
        totalViews,
        avgTimeOnPage: parseFloat(avgTimeOnPage.toFixed(1))
      },
      podcast: {
        totalDownloads,
        totalSubscribers,
        totalEpisodes,
        avgListenDuration: parseFloat(avgListenDuration.toFixed(1))
      },
      advertising: {
        totalAdSpend
      }
    };
  } catch (error) {
    console.error('Error getting marketing metrics:', error);
    throw error;
  }
};

/**
 * Export all marketing data for a user
 * @returns {Promise<Object>} Complete marketing data export
 */
export const exportMarketingData = async () => {
  try {
    const data = await getMarketingReportsData();
    const metrics = await getMarketingMetrics();
    
    return {
      data,
      metrics,
      exportedAt: new Date().toISOString(),
      version: '1.0'
    };
  } catch (error) {
    console.error('Error exporting marketing data:', error);
    throw error;
  }
};

// Export data type constants for use in components
export { DATA_TYPES };
